import type { FC } from 'react';
import React from 'react';
import { usePlannerContext } from '../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../Planner/Context/PlannerTranslationsContextProvider';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { PopupDialog } from '~/Partials/Modal/PopupDialog/PopupDialog';
import type { Theme } from '~/Utils/Theme';
import styles from './PlannerPopup.module.css';

export interface PlannerPopupProps {
    heading: string;
    body?: HtmlString;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const PlannerPopup: FC<React.PropsWithChildren<PlannerPopupProps>> = props => {
    const contextualTheme = useTheme();
    const { heading, body, theme = contextualTheme } = props;

    const { isPopupVisible, setPopupVisibility, removeAllSelectedSubjects } = usePlannerContext();
    const { popup } = usePlannerTranslations();

    const { cancel, clearSummary, closePopup } = popup;

    const handlePopupClose = () => setPopupVisibility(false);
    const handleSummaryClear = () => {
        removeAllSelectedSubjects();
        setPopupVisibility(false);
    };

    const buttonBase = {
        tier: ButtonTier.Primary,
        size: ButtonSize.Medium,
        theme,
    };

    return (
        <PopupDialog
            isOpen={isPopupVisible}
            onClose={handlePopupClose}
            translations={{ closeModal: closePopup }}
            title={heading}
        >
            <div>
                {body && <HtmlDiv className={styles.Body} html={body} />}
                <div className={styles.Controls}>
                    <Button {...buttonBase} type={ButtonType.Solid} onClick={handleSummaryClear}>
                        {clearSummary}
                    </Button>
                    <Button {...buttonBase} type={ButtonType.Outline} onClick={handlePopupClose}>
                        {cancel}
                    </Button>
                </div>
            </div>
        </PopupDialog>
    );
};
